import type { TextInputSuffix } from '@ksyos/design-system';
import {
    ActionBar,
    Box,
    Button,
    Card,
    Col,
    Heading,
    Pagination,
    Row,
    Table,
    TableBody,
    TableCell,
    TableCellSelectRow,
    TableContainer,
    TableHead,
    TableHeader,
    TableHeaderSelectAll,
    TableRow,
    TextInput,
} from '@ksyos/design-system';
import bind from 'bind-decorator';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { ManagerAuthorization } from '@common/enums';
import { KgbExportType } from '@common/enums/kgbExportType';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { assertHasAuthorization } from '@frontend/utils/assertHasAuthorization';
import type { IGroupFilters } from '../../../../common/validation/group';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { AddButtonHeader } from '../../elements/AddButtonHeader';
import { BadgeWithLabel } from '../../elements/BadgeWithLabel';
import { TextWithMatchingTermInBold } from '../../elements/common';
import { Component } from '../../elements/Component';
import { KgbExportModal } from '../../elements/KgbExportModal';
import { FullWidthTemplate } from '../../templates/FullWidthTemplate';
import { GroupListSidebar } from './GroupListSidebar';

interface IGroup {
    authorization: {
        canDelete: boolean;
        canUpdate: boolean;
    };
    id: string;
    isActive: boolean;
    name: string;
    relationNumber: string;
}

interface IGroupListState {
    areAllChecked: boolean;
    authorization: {
        canAdd: boolean;
    };
    filters: IGroupFilters;
    isSearching: boolean;
    items: IGroup[];
    itemsPerPage: number;
    page: number;
    selectedGroupId: string;
    selectedItems: string[];
    showExportModal: boolean;
    showSelectedGroupSidebar: boolean;
    sortDirection: 'ASC' | 'DESC';
    sortKey: 'name';
    total: number;
}

class WrappedGroupsList extends Component<IWithRouterProps, IGroupListState> {
    constructor(props: IWithRouterProps, context: {}) {
        super(props, context);
        const page = this.props.router.searchParams.get('page') ?? '1';
        const defaultItemsPerPage = 15;
        this.state = {
            selectedItems: [],
            items: [],
            authorization: {
                canAdd: false,
            },
            page: Number(page),
            areAllChecked: false,
            sortDirection: 'ASC',
            itemsPerPage: defaultItemsPerPage,
            showExportModal: false,
            sortKey: 'name',
            selectedGroupId: '',
            isSearching: true,
            showSelectedGroupSidebar: false,
            total: 0,
            filters: {},
        };
    }

    public async componentDidMount() {
        await this.search();
    }

    public render() {
        assertHasAuthorization(ManagerAuthorization.groupRead);
        const suffixProp: TextInputSuffix = {
            icon: 'search',
            labelText: 'aria labelled text',
            onSuffixClick: async () => await this.applySearchFilter(),
        };

        return (
            <FullWidthTemplate>
                <AddButtonHeader
                    buttonText={translateKey('common.addItem', translateKey('groups.group'))}
                    hideButton={!this.state.authorization.canAdd}
                    link="/groups/add"
                    outlined
                    style={{ marginTop: 40 }}
                    title={<Heading>{translateKey('groups.groups')}</Heading>}
                />
                <Card>
                    <Card.Body>
                        <Row
                            columnGap={4}
                            style={{ flexGrow: 1 }}
                        >
                            <Col span={4}>
                                <TextInput
                                    onChange={async (e) =>
                                        await this.handleChange('filters', { searchText: e.target.value })
                                    }
                                    onEnterKeyPress={async () => await this.applySearchFilter()}
                                    onReset={async () => {
                                        await this.handleChange('filters', { searchText: '' });
                                        await this.search();
                                    }}
                                    placeholder="Naam van de (zorg)groep..."
                                    suffix={suffixProp}
                                    value={this.state.filters.searchText ?? ''}
                                />
                            </Col>
                            <Col span={4}>
                                <TextInput
                                    onChange={async (e) =>
                                        await this.handleChange('filters', { relationNumber: e.target.value })
                                    }
                                    onEnterKeyPress={async () => await this.applySearchFilter()}
                                    onReset={async () => {
                                        await this.handleChange('filters', { relationNumber: '' });
                                        await this.search();
                                    }}
                                    placeholder={`${translateKey('organization.relationNumber')}...`}
                                    suffix={suffixProp}
                                    value={this.state.filters.relationNumber ?? ''}
                                />
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
                <TableContainer>
                    <Table spacing="medium">
                        <TableHead>
                            <TableRow>
                                <TableHeaderSelectAll
                                    checked={this.state.selectedItems.length > 0 || this.state.areAllChecked}
                                    indeterminate={!this.state.areAllChecked}
                                    onSelect={async () =>
                                        await this.setState({
                                            selectedItems: [],
                                            areAllChecked: !this.state.areAllChecked,
                                        })
                                    }
                                />
                                <TableHeader
                                    isSortable
                                    isSortHeader={this.state.sortKey === 'name'}
                                    onClick={async () => await this.handleSort('name')}
                                    sortDirection={this.state.sortDirection}
                                >
                                    {translateKey('common.name')}
                                </TableHeader>
                                <TableHeader>{translateKey('organization.relationNumber')}</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {this.state.items.map((item) => (
                                <TableRow
                                    key={item.id}
                                    onClick={() =>
                                        this.setState({
                                            selectedGroupId: item.id,
                                            showSelectedGroupSidebar: true,
                                        })
                                    }
                                >
                                    <TableCellSelectRow
                                        isSelected={this.isRowSelected(item.id) || this.state.areAllChecked}
                                        onClick={(e) => e.stopPropagation()}
                                        onSelect={async () => await this.handleSelectRow(item.id)}
                                    />
                                    <TableCell>
                                        <TextWithMatchingTermInBold text={item.name} />
                                    </TableCell>
                                    <TableCell>
                                        <TextWithMatchingTermInBold text={item.relationNumber} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Pagination
                    isDisabled={this.state.isSearching}
                    onChange={async ({ page, pageSize }) => {
                        await this.setState({ itemsPerPage: pageSize });
                        await this.applySearchFilter();
                        this.handleNavigation(page);
                    }}
                    page={this.state.page}
                    pageSize={this.state.itemsPerPage}
                    totalItems={this.state.total}
                />
                <ActionBar
                    isOpen={this.state.selectedItems.length !== 0 || this.state.areAllChecked}
                    onClose={async () => await this.setState({ selectedItems: [], areAllChecked: false })}
                >
                    <Box
                        alignItems="center"
                        display="flex"
                        gap={3}
                        justifyContent="space-between"
                    >
                        <BadgeWithLabel
                            color="inverse"
                            label={translateKey('common.isSelected')}
                            value={
                                this.state.areAllChecked
                                    ? this.state.total.toString()
                                    : this.state.selectedItems.length.toString()
                            }
                        />
                        <Button
                            onClick={async () => await this.setState({ showExportModal: true })}
                            variant="solid"
                        >
                            {translateKey('common.export')}
                        </Button>
                    </Box>
                </ActionBar>
                <KgbExportModal
                    areAllChecked={this.state.areAllChecked}
                    deselectItems={() => this.setState({ selectedItems: [], areAllChecked: false })}
                    exportType={KgbExportType.group}
                    filters={this.state.areAllChecked ? this.state.filters : undefined}
                    isOpen={this.state.showExportModal}
                    onClose={() => this.setState({ showExportModal: false })}
                    selectedItems={this.state.areAllChecked ? undefined : this.state.selectedItems}
                    title={translateKey(
                        'groups.exportGroups',
                        this.state.areAllChecked
                            ? this.state.total.toString()
                            : this.state.selectedItems.length.toString()
                    )}
                />
                <GroupListSidebar
                    groupId={this.state.selectedGroupId}
                    isVisible={this.state.showSelectedGroupSidebar}
                    onClose={async () => await this.setState({ showSelectedGroupSidebar: false })}
                />
            </FullWidthTemplate>
        );
    }

    @bind
    private async handleNavigation(page: number) {
        // TODO CS-18548 This should emulate the custom pushHistory
        this.props.router.navigate('#search', { state: { page } });
        await this.setState({ page });
        await this.search();
    }

    private async handleChange(name: string, value: any) {
        await this.setState((prevState) => ({ ...prevState, page: 1, [name]: value }));
    }

    private async search() {
        await this.setState({ isSearching: true });
        let url = `/api/groups?includeItemAuthorizations=canUpdate,canDelete&includeListAuthorizations=canAdd&page=${this.state.page}&sortBy=${this.state.sortKey}&sortDirection=${this.state.sortDirection}&limit=${this.state.itemsPerPage}`;
        if (this.state.filters.searchText) {
            url += `&searchText=${this.state.filters.searchText}`;
        }
        if (this.state.filters.relationNumber) {
            url += `&searchRelationNumber=${this.state.filters.relationNumber}`;
        }
        const result = await fetchJson(url, 'GET');
        await this.setState({
            items: result.json.items,
            authorization: result.json.authorization,
            isSearching: false,
            total: result.json.total,
        });
    }

    private async handleSort(key: 'name') {
        await this.setState({
            sortDirection: this.state.sortDirection === 'ASC' && this.state.sortKey === key ? 'DESC' : 'ASC',
            sortKey: key,
        });
        await this.search();
    }

    private isRowSelected(selectedId: string) {
        return this.state.selectedItems.includes(selectedId);
    }

    private async applySearchFilter() {
        // TODO CS-18548 This should emulate the custom pushHistory
        this.props.router.navigate('#search', { state: { page: 1 } });
        await this.setState({ page: 1 });
        await this.search();
    }

    private async handleSelectRow(id: string) {
        let newSelectedItems = [...this.state.selectedItems];

        if (this.state.areAllChecked) {
            newSelectedItems = this.state.items.map((groups) => groups.id).filter((groupsId) => groupsId !== id);
            await this.setState({ areAllChecked: false, selectedItems: newSelectedItems });
        } else if (this.isRowSelected(id)) {
            const index = newSelectedItems.indexOf(id);
            newSelectedItems.splice(index, 1);
        } else {
            newSelectedItems.push(id);
        }

        await this.setState({ selectedItems: newSelectedItems });
    }
}

export const GroupsList = withRouter(WrappedGroupsList);
